import {
  type BooleanInput,
  coerceBooleanProperty,
} from '@angular/cdk/coercion';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  type OnDestroy,
} from '@angular/core';
import {
  type ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ISelectOption } from '@principle-theorem/shared';
import { isNil, isNumber, isString } from 'lodash';
import { BehaviorSubject, ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TypedFormControl } from '../forms/typed-form-group';
import { PipesModule } from '../pipes/pipes.module';
import { TrackByFunctions } from '../track-by';

@Component({
  selector: 'pt-settings-menu-select',
  imports: [
    CommonModule,
    FormsModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatIconModule,
    MatFormFieldModule,
    MatTooltipModule,
    PipesModule,
  ],
  templateUrl: './settings-menu-select.component.html',
  styleUrls: ['./settings-menu-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SettingsMenuSelectComponent),
      multi: true,
    },
  ],
})
export class SettingsMenuSelectComponent
  implements ControlValueAccessor, OnDestroy
{
  private _onDestroy$: Subject<void> = new Subject();
  private _onChange: (
    value?: string | number | ISelectOption<string | number>
  ) => void;
  disabled$ = new BehaviorSubject<boolean>(false);
  emitEvent$ = new BehaviorSubject<boolean>(true);
  options$ = new ReplaySubject<
    (string | number | ISelectOption<string | number>)[]
  >(1);
  trackByOption = TrackByFunctions.index<
    string | number | ISelectOption<string | number>
  >();
  @Input() label: string;
  @Input() tooltip?: string;
  formCtrl = new TypedFormControl<string | number>();

  @Input()
  set disabled(disabled: BooleanInput) {
    this.disabled$.next(coerceBooleanProperty(disabled));
  }

  @Input()
  set options(options: (string | number | ISelectOption<string | number>)[]) {
    if (options) {
      this.options$.next(options);
    }
  }

  @Input()
  set emitEvent(emitEvent: boolean) {
    if (!isNil(emitEvent)) {
      this.emitEvent$.next(emitEvent);
    }
  }

  constructor() {
    this.formCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((newValue) => {
        if (this._onChange) {
          this._onChange(newValue);
        }
      });
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  writeValue(value: string): void {
    this.formCtrl.setValue(value ?? '', { emitEvent: this.emitEvent$.value });
  }

  registerOnChange(fn: () => void): void {
    this._onChange = fn;
  }

  registerOnTouched(_fn: () => void): void {
    //
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formCtrl.disable();
      return;
    }
    this.formCtrl.enable();
  }

  optionToLabel(
    option: string | number | ISelectOption<string | number>
  ): string {
    if (isString(option) || isNumber(option)) {
      return option.toString();
    }

    return option.label;
  }

  optionToValue(
    option: string | number | ISelectOption<string | number>
  ): string | number {
    if (isString(option) || isNumber(option)) {
      return option;
    }

    return option.value;
  }
}
